<template>
  <!--
    AS CONFIGURAÇÕES, em papel (D15). A versão antiga era o modal azul de aplicativo com ⚙️ e 🎵 de
    emoji nas "decorações" e bandeiras-emoji nos idiomas (UI-10). Esta usa a moldura do jogo, as
    abas do primitivo e sprites — e a única exuberância que ficou é a que importa: o nome da música
    tocando.
  -->
  <FishModal :aberto="show" :titulo="$t('configs.title')" @fechar="$emit('close')">
    <FishTabs v-model="activeTab" :abas="abas" estilo="pilula" class="configs__abas" />

    <!-- ==================== Idioma ==================== -->
    <div v-if="activeTab === 'language'" class="configs__secao">
      <div class="configs__linguas">
        <FishButton
          v-for="lang in availableLanguages"
          :key="lang.code"
          :variante="currentLanguage === lang.code ? 'primario' : 'neutro'"
          tamanho="pequeno"
          som="clique"
          @click="changeLanguage(lang.code)"
        >
          {{ lang.name }}
        </FishButton>
      </div>
    </div>

    <!-- ==================== Som ==================== -->
    <div v-if="activeTab === 'sound'" class="configs__secao">
      <div class="configs__ajuste">
        <div class="configs__ajuste-cabecalho">
          <span>{{ $t('configs.music') }}</span>
          <button type="button" class="configs__mudo" :aria-pressed="isMusicMuted ? 'true' : 'false'" @click="toggleMusic">
            <FishIcon :nome="isMusicMuted ? 'volume-mute' : 'volume-up'" tamanho="pequeno" />
          </button>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          :value="musicVolume * 100"
          :disabled="isMusicMuted"
          :aria-label="$t('configs.music')"
          @input="updateMusicVolume"
        >
      </div>

      <div class="configs__ajuste">
        <div class="configs__ajuste-cabecalho">
          <span>{{ $t('configs.soundEffects') }}</span>
          <button type="button" class="configs__mudo" :aria-pressed="isSoundMuted ? 'true' : 'false'" @click="toggleSound">
            <FishIcon :nome="isSoundMuted ? 'volume-mute' : 'volume-up'" tamanho="pequeno" />
          </button>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          :value="soundVolume * 100"
          :disabled="isSoundMuted"
          :aria-label="$t('configs.soundEffects')"
          @input="updateSoundVolume"
        >
      </div>

      <!-- A vitrola: o que está tocando, e as três teclas. -->
      <div class="configs__ajuste">
        <div class="configs__ajuste-cabecalho">
          <span>{{ $t('configs.musicControls') }}</span>
        </div>
        <p v-if="currentMusicInfo" class="configs__faixa">
          <FishIcon nome="music" tamanho="miudo" />
          <span class="configs__faixa-nome">{{ currentMusicInfo.title }} — {{ currentMusicInfo.artist }}</span>
        </p>
        <p v-if="autoplayFailed" class="configs__aviso">{{ $t('configs.autoplayWarning') }}</p>
        <div class="configs__vitrola">
          <FishButton tamanho="pequeno" icone="step-backward" som="clique" :aria-label="'←'" @click="prevMusic" />
          <FishButton tamanho="pequeno" :icone="isPlaying ? 'pause' : 'play'" som="clique" @click="togglePlayPause" />
          <FishButton tamanho="pequeno" icone="step-forward" som="clique" @click="nextMusic" />
        </div>
      </div>
    </div>

    <!-- ==================== Pescador ==================== -->
    <div v-if="activeTab === 'player'" class="configs__secao">
      <p class="configs__rotulo">{{ $t('configs.changePassword') }}</p>

      <form class="configs__form" @submit.prevent="changePassword">
        <div v-for="campo in camposDeSenha" :key="campo.chave" class="configs__senha">
          <input
            :type="campo.visivel ? 'text' : 'password'"
            :value="passwordForm[campo.chave]"
            :placeholder="$t(campo.placeholder)"
            autocomplete="off"
            @input="passwordForm[campo.chave] = $event.target.value"
          >
          <button type="button" class="configs__olho" :aria-label="$t(campo.placeholder)" @click="campo.visivel = !campo.visivel">
            <FishIcon :nome="campo.visivel ? 'eye-slash' : 'eye'" tamanho="pequeno" />
          </button>
        </div>

        <p v-if="passwordError" class="configs__erro">{{ passwordError }}</p>
        <p v-if="passwordSuccess" class="configs__sucesso">{{ passwordSuccess }}</p>

        <FishButton tipo="submit" variante="primario" bloco som="clique">
          {{ $t('configs.updatePassword') }}
        </FishButton>
      </form>
    </div>

    <template #acoes>
      <FishButton variante="perigo" tamanho="pequeno" icone="sign-out-alt" som="clique" @click="handleLogout">
        {{ $t('configs.logout') }}
      </FishButton>
      <FishButton tamanho="pequeno" som="clique" @click="$emit('close')">
        {{ $t('configs.close') }}
      </FishButton>
    </template>
  </FishModal>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import { useI18n } from 'vue-i18n'
import * as v2 from '@/core/services/api/v2'
import { FishModal, FishTabs, FishButton, FishIcon } from '@/shared/ui'

export default {
  name: 'ConfigsModal',
  components: { FishModal, FishTabs, FishButton, FishIcon },
  setup() {
    const { locale } = useI18n()
    return { locale }
  },
  data() {
    return {
      activeTab: 'language',
      // Sem bandeira-emoji (UI-10): o nome da língua, na própria língua, já é a bandeira.
      availableLanguages: [
        { code: 'pt', name: 'Português' },
        { code: 'en', name: 'English' },
        { code: 'es', name: 'Español' },
        { code: 'zh', name: '中文' }
      ],
      passwordForm: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      camposDeSenha: [
        { chave: 'currentPassword', placeholder: 'configs.currentPassword', visivel: false },
        { chave: 'newPassword', placeholder: 'configs.newPassword', visivel: false },
        { chave: 'confirmPassword', placeholder: 'configs.confirmPassword', visivel: false },
      ],
      passwordError: '',
      passwordSuccess: ''
    }
  },
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  computed: {
    ...mapState('SoundController', [
      'musicVolume',
      'soundVolume',
      'isMusicMuted',
      'isSoundMuted',
      'currentMusic',
      'autoplayFailed',
      'currentMusicInfo',
      'isPlaying'
    ]),
    abas() {
      return [
        { id: 'language', rotulo: this.$t('configs.languageTab'), icone: 'language' },
        { id: 'sound', rotulo: this.$t('configs.soundTab'), icone: 'volume-up' },
        { id: 'player', rotulo: this.$t('configs.playerTab'), icone: 'user' },
      ];
    },
    currentLanguage() {
      return this.locale
    }
  },
  methods: {
    ...mapMutations('SoundController', [
      'SET_MUSIC_VOLUME',
      'SET_SOUND_VOLUME',
      'TOGGLE_MUSIC_MUTE',
      'TOGGLE_SOUND_MUTE'
    ]),
    ...mapActions('language', ['setLanguage']),
    updateMusicVolume(event) {
      this.SET_MUSIC_VOLUME(event.target.value / 100)
      this.$store.dispatch('SoundController/updateVolumes')
    },
    updateSoundVolume(event) {
      this.SET_SOUND_VOLUME(event.target.value / 100)
    },
    toggleMusic() {
      this.TOGGLE_MUSIC_MUTE()
      this.$store.dispatch('SoundController/updateVolumes')
    },
    toggleSound() {
      this.TOGGLE_SOUND_MUTE()
    },
    nextMusic() {
      this.$store.dispatch('SoundController/switchMusic', 'next')
    },
    prevMusic() {
      this.$store.dispatch('SoundController/switchMusic', 'prev')
    },
    togglePlayPause() {
      if (this.isPlaying) {
        this.$store.dispatch('SoundController/pauseMusic')
      } else {
        this.$store.dispatch('SoundController/resumeMusic')
      }
    },
    handleLogout() {
      // Quem limpa a sessão é o `auth/logout` (`v2.limparSessao`).
      this.$store.dispatch('auth/logout');

      // Redirect to login page — o login mora em `/`, `/login` não é rota.
      this.$router.push('/');

      // Close modal
      this.$emit('close');
    },
    changeLanguage(lang) {
      this.locale = lang
      this.setLanguage(lang)
      localStorage.setItem('userLanguage', lang)
    },
    /**
     * Troca de senha, no V2. O servidor derruba TODAS as sessões — esta
     * inclusive — e `v2.trocarSenha` já limpa a sessão local: depois do
     * sucesso só resta avisar e mandar o jogador de volta ao login.
     */
    async changePassword() {
      this.passwordError = ''
      this.passwordSuccess = ''

      // Validação local: o V2 não recebe `passwordConfirmation` — conferir a
      // digitação é papel do cliente, antes de gastar a viagem.
      if (this.passwordForm.newPassword !== this.passwordForm.confirmPassword) {
        this.passwordError = this.$t('configs.passwordMismatch')
        return
      }

      try {
        await v2.trocarSenha(
          this.passwordForm.currentPassword,
          this.passwordForm.newPassword
        )

        // Sessão derrubada no servidor e limpa localmente: o estado do Vuex
        // ainda diz "logado" — zera para o guard do router não deixar o
        // jogador numa tela que só faria chamadas 401.
        v2.esquecerRestauracao()
        this.$store.commit('auth/CLEAR_AUTH')

        this.passwordSuccess = this.$t('configs.passwordChanged')
        this.passwordForm = {
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }

        // Um respiro para o aviso ser lido antes do redirect ao login (`/`).
        setTimeout(() => {
          this.$emit('close')
          this.$router.push('/')
        }, 2000)
      } catch (error) {
        // 401 = senha atual incorreta — o caso comum merece a mensagem clara.
        if (error.response?.status === 401) {
          this.passwordError = this.$t('configs.wrongPassword')
        } else {
          this.passwordError =
            error.response?.data?.message ||
            error.response?.data?.error?.message ||
            error.message ||
            this.$t('configs.wrongPassword')
        }
        console.error('Password change error:', error)
      }
    }
  }
}
</script>

<style scoped>
.configs__abas {
  margin-bottom: var(--e-3);
}

.configs__secao {
  display: flex;
  flex-direction: column;
  gap: var(--e-3);
  min-height: 220px;
}

.configs__linguas {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--e-2);
}

.configs__ajuste {
  border-bottom: 1px dashed var(--papel-sombra);
  padding-bottom: var(--e-2);
}

.configs__ajuste:last-child {
  border-bottom: none;
}

.configs__ajuste-cabecalho {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--e-2);
  margin-bottom: var(--e-1);
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  color: var(--tinta);
}

.configs__mudo {
  background: none;
  border: none;
  padding: var(--e-1);
  cursor: pointer;
  color: var(--tinta);
}

/* O controle deslizante na tinta do jogo: `accent-color` pinta trilho e botão sem redesenhar o
 * controle — que continua nativo, acessível e com o comportamento do sistema. */
.configs__secao input[type='range'] {
  width: 100%;
  accent-color: var(--latao);
}

.configs__secao input[type='range']:disabled {
  opacity: 0.4;
}

.configs__faixa {
  margin: 0 0 var(--e-2);
  display: flex;
  align-items: center;
  gap: var(--e-1);
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.configs__faixa-nome {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.configs__aviso {
  margin: 0 0 var(--e-2);
  font-size: var(--f-1);
  color: var(--cor-aviso);
}

.configs__vitrola {
  display: flex;
  justify-content: center;
  gap: var(--e-2);
}

.configs__rotulo {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  color: var(--tinta-fraca);
  border-bottom: 2px dashed var(--papel-sombra);
  padding-bottom: var(--e-1);
}

.configs__form {
  display: flex;
  flex-direction: column;
  gap: var(--e-2);
}

/* O campo de senha: papel rebaixado com borda de madeira, como o contador da loja. */
.configs__senha {
  display: flex;
  align-items: stretch;
  border: 2px solid var(--madeira);
  background: var(--papel-frio);
}

.configs__senha input {
  flex: 1;
  border: none;
  background: none;
  padding: var(--e-2);
  font-family: var(--fonte-corpo);
  font-size: var(--f-2);
  color: var(--tinta);
  min-width: 0;
}

.configs__senha input:focus {
  outline: 2px solid var(--latao);
  outline-offset: -2px;
}

.configs__olho {
  background: none;
  border: none;
  padding: 0 var(--e-2);
  cursor: pointer;
  color: var(--tinta-fraca);
}

.configs__erro {
  margin: 0;
  font-size: var(--f-1);
  color: var(--cor-ruim);
}

.configs__sucesso {
  margin: 0;
  font-size: var(--f-1);
  color: var(--cor-bom);
}
</style>
